/**
 * GifEdit — one edit of an animated GIF: which frames, how fast, which part of the
 * picture, what size, what shape. The /gif-trim editor's engine.
 *
 *   plan = GifEdit.plan(info, settings)   pure: what the result will be (sizes, frames,
 *                                         delays, which encoder) — the page shows it live
 *   out  = GifEdit.render(bytes, plan, o) decode → transform → encode, frame by frame
 *
 * Settings (all optional):
 *   start, end        first/last source frame kept (inclusive)
 *   mode              'normal' | 'reverse' | 'bounce'
 *   speed             ×; past the 2 cs floor frames are dropped (GifCore.planSpeed)
 *   plays             0 = forever, N = the animation plays N times
 *   rotate            0 | 90 | 180 | 270 (clockwise), flipH, flipV — applied FIRST: the page shows the
 *                     turned picture and the person crops what they see
 *   crop              {x,y,w,h} in VIEW pixels (the turned picture, full resolution)
 *   width, height     output size of the cropped view; default = the crop
 *   shape             'rect' | 'round' | 'ellipse' | 'heart' | 'star' | 'hexagon'
 *   radius            corner radius for 'round', share of the shorter side (0..0.5)
 *   edge              'hard'   transparent outside, every pixel in or out
 *                     'smooth' transparent outside, the edge blended into `matte`
 *                     'fill'   no transparency: outside painted with `matte`, smooth edge
 *   matte             [r,g,b]
 *
 * Which encoder (plan.lane):
 *   'exact'      nothing in the plan creates a colour — frames, order, timing, crop,
 *                rotation, a hard-edged shape. GifExact writes the composited frames back
 *                pixel-for-pixel, a table per frame where the source had one.
 *   'perceptual' a resize or a blended edge makes colours the source never had.
 *                GifEncode (the /gif-compressor encoder) at strength 1 dE: measured on 8
 *                real GIFs at half size — same weight as gifsicle --resize, SSIMULACRA 2
 *                higher on 6 of 8 (anna 90 vs 68, text 92 vs 75).
 *   An exact render that meets a frame of more than 255 colours of its own restarts as
 *   perceptual (out.fellBack = true) — GIF cannot hold such a frame losslessly.
 *
 * Memory: never the whole animation. Forward orders stream; a reversed run is decoded in
 * segments from decoder snapshots (GifDecode resume), so a 300-frame 1080p reverse holds
 * ~2·√n frames, not n.
 *
 * Dependencies (globals in the worker, or `deps`): GifDecode, GifEncode, GifExact, GifCore.
 */
(function (root, factory) {
    var api = factory(root);
    if (typeof module === 'object' && module.exports) module.exports = api;
    if (root) root.GifEdit = api;
}(typeof self !== 'undefined' ? self : typeof globalThis !== 'undefined' ? globalThis : null, function (root) {
    'use strict';

    var SHAPES = ['rect', 'round', 'ellipse', 'heart', 'star', 'hexagon'];
    var MAX_SIDE = 4096;            // output side ceiling (GIF allows 65535; browsers and memory do not)
    var PERCEPTUAL_Q = 1;           // dE motion/lossy allowance of the perceptual lane (see header)
    var STATS_PIXELS = 6e6;         // colour statistics sample of the perceptual lane (GifCodec does the same)
    // The compressor searches deep because bytes are its goal; an editor's resize is a one-off at a fixed
    // strength. Measured on a 960×540×171 clip at half size: budget 16 → 4 and 16 → 8 candidates render in
    // 8.5 s instead of 18.7 s, +1 % bytes, the same SSIMULACRA 2 (88.7 vs 88.6).
    var SEARCH_BUDGET = 4, SEARCH_CANDIDATES = 8;

    function deps(d) {
        d = d || {};
        var g = root || {};
        return {
            GifDecode: d.GifDecode || g.GifDecode, GifEncode: d.GifEncode || g.GifEncode,
            GifExact: d.GifExact || g.GifExact, GifCore: d.GifCore || g.GifCore
        };
    }
    function clampInt(v, lo, hi) { v = Math.round(+v); if (!isFinite(v)) v = lo; return v < lo ? lo : v > hi ? hi : v; }

    /* ================================================================== */
    /* shapes: one polygon for the page's outline and the file's mask     */
    /* ================================================================== */

    /**
     * The outline of a shape in a w × h box, as a closed polygon [x0,y0,x1,y1,…] in box
     * pixels. The page draws THIS polygon over the preview and the mask is rasterised
     * from it, so what the person sees is what the file gets. null for 'rect'.
     */
    function shapePolygon(kind, w, h, radius) {
        var pts = [], i, t, N = 256;
        if (kind === 'ellipse') {
            for (i = 0; i < N; i++) { t = i / N * Math.PI * 2; pts.push(w / 2 + Math.cos(t) * w / 2, h / 2 + Math.sin(t) * h / 2); }
        } else if (kind === 'round') {
            var r = Math.max(0, Math.min(0.5, radius == null ? 0.12 : +radius)) * Math.min(w, h);
            if (r < 0.5) return null;
            var seg = 24, corners = [[w - r, r, -Math.PI / 2], [w - r, h - r, 0], [r, h - r, Math.PI / 2], [r, r, Math.PI]];
            corners.forEach(function (c) {
                for (var k = 0; k <= seg; k++) { var a = c[2] + k / seg * Math.PI / 2; pts.push(c[0] + Math.cos(a) * r, c[1] + Math.sin(a) * r); }
            });
        } else if (kind === 'heart') {
            // the classic parametric heart, scaled to fill the box
            var raw = [], minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
            for (i = 0; i < N; i++) {
                t = i / N * Math.PI * 2;
                var x = 16 * Math.pow(Math.sin(t), 3);
                var y = -(13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t));
                raw.push(x, y);
                if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y;
            }
            for (i = 0; i < raw.length; i += 2) pts.push((raw[i] - minX) / (maxX - minX) * w, (raw[i + 1] - minY) / (maxY - minY) * h);
        } else if (kind === 'star') {
            // five points, inner radius 0.45 — then stretched to the box
            var rawS = [], sx0 = Infinity, sx1 = -Infinity, sy0 = Infinity, sy1 = -Infinity;
            for (i = 0; i < 10; i++) {
                var rr = i % 2 ? 0.45 : 1, a2 = -Math.PI / 2 + i * Math.PI / 5;
                var px = Math.cos(a2) * rr, py = Math.sin(a2) * rr;
                rawS.push(px, py);
                if (px < sx0) sx0 = px; if (px > sx1) sx1 = px; if (py < sy0) sy0 = py; if (py > sy1) sy1 = py;
            }
            for (i = 0; i < rawS.length; i += 2) pts.push((rawS[i] - sx0) / (sx1 - sx0) * w, (rawS[i + 1] - sy0) / (sy1 - sy0) * h);
        } else if (kind === 'hexagon') {
            pts.push(w * 0.25, 0, w * 0.75, 0, w, h / 2, w * 0.75, h, w * 0.25, h, 0, h / 2);
        } else return null;
        return pts;
    }

    /**
     * Coverage of the polygon over a w × h pixel grid, 4 × 4 samples per pixel
     * (even-odd scanlines), as Float32Array 0..1.
     */
    function coverage(poly, w, h) {
        var cov = new Float32Array(w * h), S = 4, n = poly.length / 2, xs = [];
        for (var sy = 0; sy < h * S; sy++) {
            var y = (sy + 0.5) / S;
            xs.length = 0;
            for (var i = 0, j = n - 1; i < n; j = i++) {
                var yi = poly[i * 2 + 1], yj = poly[j * 2 + 1];
                if ((yi > y) !== (yj > y)) xs.push(poly[i * 2] + (y - yi) / (yj - yi) * (poly[j * 2] - poly[i * 2]));
            }
            xs.sort(function (a, b) { return a - b; });
            var row = Math.floor(sy / S) * w;
            for (var k = 0; k + 1 < xs.length; k += 2) {
                // samples at x = (sx + 0.5)/S inside [xs[k], xs[k+1])
                var a = Math.max(0, Math.ceil(xs[k] * S - 0.5)), b = Math.min(w * S - 1, Math.ceil(xs[k + 1] * S - 0.5) - 1);
                for (var sx = a; sx <= b; sx++) cov[row + (sx / S | 0)] += 1 / (S * S);
            }
        }
        return cov;
    }

    /* ================================================================== */
    /* plan                                                               */
    /* ================================================================== */

    function shownDelay(cs) { return cs < 2 ? 10 : cs; }

    /**
     * @param {object} info  GifDecode.parse() result
     * @param {object} s     settings (see header)
     */
    function plan(info, s, d) {
        d = deps(d);
        s = s || {};
        var n = info.frames.length, W = info.width, H = info.height;
        var start = clampInt(s.start == null ? 0 : s.start, 0, n - 1);
        var end = clampInt(s.end == null ? n - 1 : s.end, start, n - 1);
        var mode = s.mode === 'reverse' || s.mode === 'bounce' ? s.mode : 'normal';

        var seq = [], i;
        if (mode === 'reverse') for (i = end; i >= start; i--) seq.push(i);
        else {
            for (i = start; i <= end; i++) seq.push(i);
            if (mode === 'bounce') for (i = end - 1; i > start; i--) seq.push(i);
        }
        var speed = +s.speed > 0 ? +s.speed : 1;
        var sp = d.GifCore.planSpeed({ frames: seq.map(function (k) { return { delayCs: info.frames[k].delayCs }; }) }, { speed: speed });
        var order = sp.keep ? sp.keep.map(function (k) { return seq[k]; }) : seq;
        var delays = sp.delays.slice();

        // geometry: turn first (view space), then crop what the view shows
        var rotate = [0, 90, 180, 270].indexOf(+s.rotate) >= 0 ? +s.rotate : 0;
        var VW = rotate % 180 ? H : W, VH = rotate % 180 ? W : H;
        var c = s.crop || {};
        var cx = clampInt(c.x == null ? 0 : c.x, 0, VW - 1), cy = clampInt(c.y == null ? 0 : c.y, 0, VH - 1);
        var cw = clampInt(c.w == null ? VW - cx : c.w, 1, VW - cx), ch = clampInt(c.h == null ? VH - cy : c.h, 1, VH - cy);
        var rw = cw, rh = ch;
        var ow = s.width ? clampInt(s.width, 1, MAX_SIDE) : rw, oh = s.height ? clampInt(s.height, 1, MAX_SIDE) : rh;
        var shape = SHAPES.indexOf(s.shape) >= 0 ? s.shape : 'rect';
        var edge = s.edge === 'smooth' || s.edge === 'fill' ? s.edge : 'hard';
        if (shape === 'rect') edge = 'hard';
        var radius = s.radius == null ? 0.12 : Math.max(0, Math.min(0.5, +s.radius));
        var poly = shapePolygon(shape, ow, oh, radius);
        if (!poly) { shape = 'rect'; edge = 'hard'; }
        var matte = Array.isArray(s.matte) && s.matte.length === 3 ? s.matte.map(function (v) { return clampInt(v, 0, 255); }) : [255, 255, 255];
        var resized = ow !== rw || oh !== rh;

        var plays = s.plays == null ? 0 : clampInt(s.plays, 0, 65536);
        // NETSCAPE counts REPEATS after the first play (0 = forever); no block = plays once — GifCore owns it
        var loop = d.GifCore.loopFor(plays);

        var total = 0; for (i = 0; i < delays.length; i++) total += shownDelay(delays[i]);
        var srcTotal = 0; for (i = 0; i < n; i++) srcTotal += shownDelay(info.frames[i].delayCs);
        return {
            srcW: W, srcH: H, srcFrames: n, srcDurationMs: srcTotal * 10,
            start: start, end: end, mode: mode,
            order: order, delays: delays,
            speed: speed, achievedSpeed: sp.achievedSpeed, droppedFrames: sp.droppedFrames || 0,
            durationMs: total * 10,
            viewW: VW, viewH: VH,
            crop: { x: cx, y: cy, w: cw, h: ch }, cropped: cx !== 0 || cy !== 0 || cw !== VW || ch !== VH,
            rotate: rotate, flipH: !!s.flipH, flipV: !!s.flipV,
            width: ow, height: oh, resized: resized,
            shape: shape, radius: radius, edge: edge, matte: matte, polygon: poly,
            plays: plays, loop: loop,
            lane: resized || edge !== 'hard' ? 'perceptual' : 'exact'
        };
    }

    /* ================================================================== */
    /* transform                                                          */
    /* ================================================================== */

    /** Turns a composited source frame into the output frame. Returns a fresh array. */
    function Transformer(p, d) {
        this.p = p;
        var rw = p.crop.w, rh = p.crop.h;
        this.rw = rw; this.rh = rh;
        this.rz = p.resized ? new d.GifEncode.Resizer(rw, rh, p.width, p.height) : null;
        this.cov = p.polygon ? coverage(p.polygon, p.width, p.height) : null;
        this.geo = new Uint8Array(rw * rh * 4);
        this.outBytes = p.width * p.height * 4;
        this.identityGeo = !p.cropped && !p.rotate && !p.flipH && !p.flipV;
    }
    Transformer.prototype.run = function (src, W) {
        var p = this.p, c = p.crop, rw = this.rw, rh = this.rh, g;
        if (this.identityGeo) g = src;
        else {
            g = this.geo;
            var rot = p.rotate, fh = p.flipH, fv = p.flipV;
            if (!rot && !fh && !fv) {
                // a plain crop: whole rows at a time
                for (var yy = 0; yy < rh; yy++) g.set(src.subarray(((c.y + yy) * W + c.x) * 4, ((c.y + yy) * W + c.x + rw) * 4), yy * rw * 4);
                return this._finish(g);
            }
            var src32 = new Uint32Array(src.buffer, src.byteOffset, src.byteLength >> 2), g32 = new Uint32Array(g.buffer);
            var VW = p.viewW, VH = p.viewH, SH = p.srcH, SW = p.srcW;
            for (var y = 0; y < rh; y++) {
                for (var x = 0; x < rw; x++) {
                    // view pixel → undo the flips (they act on the turned picture) → undo the turn
                    var vx = c.x + x, vy = c.y + y, sx, sy;
                    if (fh) vx = VW - 1 - vx;
                    if (fv) vy = VH - 1 - vy;
                    if (rot === 90) { sx = vy; sy = SH - 1 - vx; }
                    else if (rot === 180) { sx = SW - 1 - vx; sy = SH - 1 - vy; }
                    else if (rot === 270) { sx = SW - 1 - vy; sy = vx; }
                    else { sx = vx; sy = vy; }
                    g32[y * rw + x] = src32[sy * W + sx];
                }
            }
        }
        return this._finish(g);
    };
    Transformer.prototype._finish = function (g) {
        var p = this.p;
        var out = this.rz ? this.rz.run(g).slice() : g.slice();
        var cov = this.cov;
        if (cov) {
            var edge = p.edge, m = p.matte;
            for (var i = 0, o = 0; i < cov.length; i++, o += 4) {
                var cv = cov[i];
                if (edge === 'hard') { if (cv < 0.5) out[o + 3] = 0; continue; }
                if (edge === 'smooth') {
                    if (cv <= 0) { out[o + 3] = 0; continue; }
                    if (cv >= 1 || out[o + 3] < 128) continue;
                } else if (cv >= 1 && out[o + 3] >= 128) continue;
                // blend into the matte (for 'fill' a transparent source pixel counts as matte too)
                var a = out[o + 3] >= 128 ? cv : 0;
                out[o] = Math.round(out[o] * a + m[0] * (1 - a));
                out[o + 1] = Math.round(out[o + 1] * a + m[1] * (1 - a));
                out[o + 2] = Math.round(out[o + 2] * a + m[2] * (1 - a));
                out[o + 3] = 255;
            }
        }
        return out;
    };

    /* ================================================================== */
    /* frame source: plan order → transformed frames, bounded memory      */
    /* ================================================================== */

    /**
     * Calls emit(frameRgba, k) for k = 0..order.length-1 in order. Forward runs stream;
     * a descending run is decoded segment by segment from snapshots kept during a first
     * pass (segment length ≈ √(frames in the run)).
     */
    function eachOrdered(d, bytes, info, order, xf, emit, ctl, budget) {
        var W = info.width;
        var runs = [], r = null, k;
        for (k = 0; k < order.length; k++) {
            var dir = k === 0 ? 0 : order[k] - order[k - 1];
            if (!r || (r.dir !== 0 && Math.sign(dir) !== r.dir) || dir === 0) { r = { from: k, to: k, dir: 0 }; runs.push(r); continue; }
            if (r.dir === 0) r.dir = Math.sign(dir);
            r.to = k;
        }
        var desc = runs.filter(function (x) { return x.dir < 0; });
        // Reverse and bounce: when every frame they show fits the device's budget, ONE decode
        // pass keeps them and the order is played from memory (a 171-frame bounce: 1 pass, not 3).
        if (desc.length) {
            var uniq = Array.from(new Set(order)).sort(function (a, b) { return a - b; });
            if (uniq.length * xf.outBytes <= (budget || 0)) {
                var held = new Map(), wantAll = new Set(uniq);
                d.GifDecode.frames(bytes, function (fr) {
                    if (ctl && ctl.cancelled()) throw ctl.abort();
                    if (wantAll.has(fr.index)) held.set(fr.index, xf.run(fr.rgba, W));
                }, { info: info, only: wantAll, until: uniq[uniq.length - 1] });
                for (k = 0; k < order.length; k++) emit(held.get(order[k]), k);
                return;
            }
        }
        // otherwise snapshots for every descending run, taken in one forward pass
        var snaps = {};
        if (desc.length) {
            var want = new Set(), lo = Infinity, hi = -1;
            desc.forEach(function (x) {
                var a = order[x.to], b = order[x.from];      // a < b
                var seg = Math.max(4, Math.ceil(Math.sqrt(b - a + 1)));
                x.seg = seg;
                for (var s = a; s <= b; s += seg) if (s > 0) want.add(s);
                if (a < lo) lo = a; if (b > hi) hi = b;
            });
            d.GifDecode.frames(bytes, function () { if (ctl && ctl.cancelled()) throw ctl.abort(); }, {
                info: info, until: hi, snapshotAt: want,
                onSnapshot: function (i, st) { snaps[i] = st; }
            });
        }
        for (var ri = 0; ri < runs.length; ri++) {
            var run = runs[ri];
            if (run.dir >= 0) {
                var need = new Set(), map = new Map();
                for (k = run.from; k <= run.to; k++) { need.add(order[k]); map.set(order[k], k); }
                var lastIdx = order[run.to];
                var resumeAt = null, best = -1;
                for (var key in snaps) { var at = +key; if (at <= order[run.from] && at > best) best = at; }
                if (best > 0) resumeAt = snaps[best];
                d.GifDecode.frames(bytes, function (fr) {
                    if (ctl && ctl.cancelled()) throw ctl.abort();
                    if (!need.has(fr.index)) return;
                    emit(xf.run(fr.rgba, W), map.get(fr.index));
                }, { info: info, only: need, until: lastIdx, resume: resumeAt });
            } else {
                // positions of this run's frames (a run may skip frames: reverse + a speed that drops some)
                var posOf = new Map();
                for (k = run.from; k <= run.to; k++) posOf.set(order[k], k);
                var top = order[run.from], bottom = order[run.to], seg = run.seg;
                for (var s0 = bottom + Math.floor((top - bottom) / seg) * seg; s0 >= bottom; s0 -= seg) {
                    var s1 = Math.min(top, s0 + seg - 1), held = [];
                    var want2 = new Set();
                    for (var q = s0; q <= s1; q++) if (posOf.has(q)) want2.add(q);
                    if (!want2.size) continue;
                    var st = s0 > 0 ? snaps[s0] : null;
                    d.GifDecode.frames(bytes, function (fr) {
                        if (ctl && ctl.cancelled()) throw ctl.abort();
                        if (want2.has(fr.index)) held.push({ i: fr.index, f: xf.run(fr.rgba, W) });
                    }, { info: info, until: s1, resume: st, only: want2 });
                    for (var h = held.length - 1; h >= 0; h--) emit(held[h].f, posOf.get(held[h].i));
                }
            }
        }
    }
    function rangeSet(a, b) { var s = new Set(); for (var i = a; i <= b; i++) s.add(i); return s; }

    /* ================================================================== */
    /* render                                                             */
    /* ================================================================== */

    /**
     * @param {Uint8Array} bytes
     * @param {object} p      plan()
     * @param {object} [o]    {onProgress(frac), isCancelled():bool, census: Map rgb24→count (exact lane palette),
     *                         lane: force 'perceptual', memBudget: bytes reverse/bounce may hold (default 0 =
     *                         segments from snapshots), deps}
     * @returns {{bytes:Uint8Array, width, height, frames:number, lane:string, fellBack:boolean, stats:object, ms:number}}
     */
    function render(bytes, p, o) {
        o = o || {};
        var d = deps(o.deps), t0 = Date.now();
        bytes = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
        var info = d.GifDecode.parse(bytes);
        if (!info) throw new Error('GifEdit: not a GIF');
        var ctl = {
            cancelled: function () { return !!(o.isCancelled && o.isCancelled()); },
            abort: function () { var e; try { e = new DOMException('cancelled', 'AbortError'); } catch (_) { e = new Error('cancelled'); e.name = 'AbortError'; } return e; }
        };
        var lane = o.lane === 'perceptual' ? 'perceptual' : p.lane;
        var xf = new Transformer(p, d), total = p.order.length;
        var progress = function (done, share, base) { if (o.onProgress) o.onProgress(Math.min(1, base + share * done / Math.max(1, total))); };

        if (lane === 'exact') {
            var pal = d.GifExact.globalPalette(o.census || null, bytes, info);
            var ex = new d.GifExact.Encoder(p.width, p.height, { palette: pal, loop: p.loop }, { GifEncode: d.GifEncode });
            var res = encodeStream(d, bytes, info, p, xf, ctl, function (f, delay, next) { return ex.addFrame(f, delay, next); }, function (k) { progress(k, 1, 0); }, o.memBudget);
            if (res.ok) {
                return { bytes: ex.finish(), width: p.width, height: p.height, frames: ex.stats.frames, lane: 'exact', fellBack: false, stats: ex.stats, ms: Date.now() - t0 };
            }
            // a frame with more than 255 colours of its own: start over on the perceptual lane
            var again = render(bytes, p, Object.assign({}, o, { lane: 'perceptual' }));
            again.fellBack = true;
            return again;
        }

        // perceptual: a colour pass over a sample of the output frames, then the encode
        var st = new d.GifEncode.ColorStats(), px = p.width * p.height;
        var uniq = Array.from(new Set(p.order)).sort(function (a, b) { return a - b; });
        var stepF = Math.max(1, Math.ceil(uniq.length * px / STATS_PIXELS));
        var sample = new Set(uniq.filter(function (_, i) { return i % stepF === 0; }));
        d.GifDecode.frames(bytes, function (fr) {
            if (ctl.cancelled()) throw ctl.abort();
            if (sample.has(fr.index)) st.add(xf.run(fr.rgba, info.width), px);
        }, { info: info, only: sample, until: uniq[uniq.length - 1] });
        var palette = d.GifEncode.quantize(st.points(), 255);
        if (o.onProgress) o.onProgress(0.15);
        var enc = new d.GifEncode.Encoder(p.width, p.height, palette, { motion: PERCEPTUAL_Q, lossy: PERCEPTUAL_Q, loop: p.loop, budget: SEARCH_BUDGET, maxc: SEARCH_CANDIDATES });
        encodeStream(d, bytes, info, p, xf, ctl, function (f, delay, next) { enc.addFrame(f, delay, next); return true; }, function (k) { progress(k, 0.85, 0.15); }, o.memBudget);
        return { bytes: enc.finish(), width: p.width, height: p.height, frames: enc.frames || p.order.length, lane: 'perceptual', fellBack: false, stats: enc.stats, ms: Date.now() - t0 };
    }

    /** Feeds an encoder one frame behind (it needs the NEXT frame to plan disposal). */
    function encodeStream(d, bytes, info, p, xf, ctl, add, onK, budget) {
        var prev = null, prevDelay = 0, ok = true, count = 0;
        eachOrdered(d, bytes, info, p.order, xf, function (f, k) {
            if (!ok) return;
            if (prev) ok = add(prev, prevDelay, f);
            prev = f; prevDelay = p.delays[k];
            count++;
            if ((count & 3) === 0) onK(count);
        }, ctl, budget);
        if (ok && prev) ok = add(prev, prevDelay, null);
        return { ok: ok };
    }

    /**
     * Carry a crop rectangle through a turn or a flip of the view, so the same part of the
     * picture stays selected. op: 'cw' | 'ccw' | 'flipH' | 'flipV'; VW×VH = the view BEFORE.
     */
    function moveRect(r, VW, VH, op) {
        if (op === 'cw') return { x: VH - r.y - r.h, y: r.x, w: r.h, h: r.w };
        if (op === 'ccw') return { x: r.y, y: VW - r.x - r.w, w: r.h, h: r.w };
        if (op === 'flipH') return { x: VW - r.x - r.w, y: r.y, w: r.w, h: r.h };
        if (op === 'flipV') return { x: r.x, y: VH - r.y - r.h, w: r.w, h: r.h };
        return r;
    }
    /** The rotation and flips after one more turn/flip: a flip done on a turned view stays a flip of the VIEW. */
    function turn(state, op) {
        var r = state.rotate || 0, fh = !!state.flipH, fv = !!state.flipV;
        if (op === 'cw' || op === 'ccw') {
            r = (r + (op === 'cw' ? 90 : 270)) % 360;
            // flips act on the view: turning swaps which axis they mirror
            if (fh !== fv) { var t = fh; fh = fv; fv = t; }
        } else if (op === 'flipH') fh = !fh;
        else if (op === 'flipV') fv = !fv;
        return { rotate: r, flipH: fh, flipV: fv };
    }

    return {
        plan: plan, render: render, shapePolygon: shapePolygon, coverage: coverage, moveRect: moveRect, turn: turn,
        Transformer: Transformer, SHAPES: SHAPES, MAX_SIDE: MAX_SIDE, PERCEPTUAL_Q: PERCEPTUAL_Q
    };
}));
